'use client'

import { AlertCircle, CheckCircle, Info, X, XCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { type ToastNotification } from '@/lib/toast-notifications'
import { buildAgentDashboardChatUrl } from '@/lib/utils'

interface NotificationToastProps {
  notification: ToastNotification
  onDismiss: (id: string) => void
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
}

export function NotificationToast({
  notification,
  onDismiss,
  position = 'top-right',
}: NotificationToastProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleDismiss = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => {
      // Only remove from UI - do NOT dismiss database notification
      onDismiss(notification.id)
    }, 300)
  }, [onDismiss, notification.id])

  useEffect(() => {
    // Auto-dismiss non-urgent toast notifications after 5 seconds
    // This ONLY removes the toast from UI, NOT from database
    if (!notification.urgent) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [notification.urgent, handleDismiss])

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />
    }
  }

  const getBgColor = () => {
    const baseClasses = notification.urgent ? 'ring-2' : ''
    switch (notification.type) {
      case 'success':
        return `bg-green-50 border-green-200 ${notification.urgent ? 'ring-green-500' : ''} ${baseClasses}`
      case 'error':
        return `bg-red-50 border-red-200 ${notification.urgent ? 'ring-red-500' : ''} ${baseClasses}`
      case 'warning':
        return `bg-yellow-100 border-yellow-300 ${notification.urgent ? 'ring-yellow-500' : ''} ${baseClasses}`
      case 'info':
      default:
        return `bg-blue-50 border-blue-200 ${notification.urgent ? 'ring-blue-500' : ''} ${baseClasses}`
    }
  }

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4'
      case 'bottom-right':
        return 'bottom-4 right-4'
      case 'bottom-left':
        return 'bottom-4 left-4'
      case 'top-right':
      default:
        return 'top-4 right-4'
    }
  }

  const getAnimationClasses = () => {
    const baseClasses = 'transition-all duration-300 ease-in-out'

    if (isExiting) {
      return `${baseClasses} opacity-0 transform translate-x-full`
    }

    if (isVisible) {
      return `${baseClasses} opacity-100 transform translate-x-0`
    }

    return `${baseClasses} opacity-0 transform translate-x-full`
  }

  return (
    <div
      className={`fixed z-50 max-w-sm w-full pointer-events-auto ${getPositionClasses()} ${getAnimationClasses()}`}
    >
      <div className={`rounded-lg border p-4 shadow-lg ${getBgColor()}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">{getIcon()}</div>

          <div className="ml-3 flex-1">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {notification.title}
                  {notification.urgent && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      Urgent
                    </span>
                  )}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  {notification.message}
                </p>
              </div>

              <button
                type="button"
                onClick={handleDismiss}
                className="ml-4 flex-shrink-0 text-gray-600 hover:text-gray-900 focus:outline-none"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar for auto-dismiss */}
        {!notification.urgent && (
          <div className="mt-3 w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-gray-400 h-1 rounded-full transition-all duration-5000 ease-linear"
              style={{
                width: isVisible ? '0%' : '100%',
                transitionDuration: isVisible ? '5000ms' : '0ms',
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// Container component for managing temporary toast notifications ONLY
// This component should ONLY be used for the in-memory toast system,
// NOT for persistent database notifications
export function NotificationToastContainer() {
  const { useToastNotificationStore } = require('@/lib/toast-notifications')
  const toasts = useToastNotificationStore((state: any) => state.toasts)
  const dismissToast = useToastNotificationStore(
    (state: any) => state.dismissToast
  )
  const [visibleToasts, setVisibleToasts] = useState<any[]>([])

  useEffect(() => {
    // Show temporary toasts from the toast array (NOT database notifications)
    setVisibleToasts(toasts.slice(0, 5)) // Limit to 5 toasts at once
  }, [toasts])

  const handleDismiss = (id: string) => {
    // Only dismiss the temporary toast - does NOT affect database notifications
    dismissToast(id)
    setVisibleToasts(prev => prev.filter((n: any) => n.id !== id))
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {visibleToasts.map((toast: any, index: number) => (
        <div
          key={toast.id}
          className="pointer-events-auto"
          style={{
            transform: `translateY(${index * 80}px)`,
          }}
        >
          <NotificationToast
            notification={toast}
            onDismiss={handleDismiss}
            position="top-right"
          />
        </div>
      ))}
    </div>
  )
}

// Hook for triggering TEMPORARY toast notifications
// For persistent notifications, use the database notification system instead
export function useSystemNotifications() {
  const { useToastNotificationStore } = require('@/lib/toast-notifications')

  const addToast = (toast: any) => {
    useToastNotificationStore.getState().addToast(toast)
  }

  const notifyHandoverRequest = (sessionId: string, customerName?: string) => {
    // This creates a TEMPORARY toast only
    // The persistent database notification is created by the backend
    addToast({
      type: 'warning',
      title: 'New Handover Request',
      message: `${customerName || 'A customer'} has requested agent assistance`,
      urgent: true,
    })
  }

  const notifyAgentAssignment = (sessionId: string, agentName: string) => {
    addToast({
      type: 'info',
      title: 'Agent Assigned',
      message: `${agentName} has been assigned to handle the session`,
    })
  }

  const notifySessionCompleted = (sessionId: string, rating?: number) => {
    addToast({
      type: 'success',
      title: 'Session Completed',
      message: rating
        ? `Session completed with ${rating}/5 rating`
        : 'Session has been completed',
    })
  }

  const notifySystemAlert = (
    message: string,
    type: 'error' | 'warning' = 'warning'
  ) => {
    addToast({
      type,
      title: 'System Alert',
      message,
      urgent: type === 'error',
    })
  }

  const notifyAgentStatusChange = (agentName: string, isOnline: boolean) => {
    addToast({
      type: 'info',
      title: 'Agent Status Update',
      message: `${agentName} is now ${isOnline ? 'online' : 'offline'}`,
    })
  }

  return {
    notifyHandoverRequest,
    notifyAgentAssignment,
    notifySessionCompleted,
    notifySystemAlert,
    notifyAgentStatusChange,
  }
}
